import type { TrucklistResponseType } from '#app/broker/trucklist/types'
import { DefaultFilters, type TrucklistFilterData } from '#app/broker/trucklist/utils/utils'
import { getErrorResponse } from '#app/shared/apis/restClient'
import { machineLogger } from '#app/shared/contexts/AppContext'
import type { AuthStateViewType } from '#app/shared/types/AuthTypes'
import { createActorContext } from '@xstate/react'
import { createStore } from '@xstate/store'
import { useSelector } from '@xstate/store/react'
import { userMachine } from './userMachine'
import { getFiltersToSave, loadSavedFilters, saveFiltersToLocalStorage } from './utils'

interface TrucklistState {
  routeKey?: string
  isLoading: boolean
  initialLoad: boolean
  response?: TrucklistResponseType
  error: Error | null
  incorrectAccountType?: boolean
  filters: TrucklistFilterData
  lastSearchFilters: TrucklistFilterData
}

type GlobalStateType = {
  trucklist: TrucklistState
}

type GlobalStateEventTypeMap = {
  TrucklistSearch: object
  TrucklistClearFilters: object
  TrucklistUpdateFilters: {
    fieldMap: Partial<TrucklistFilterData>
  }
  TrucklistLoad: {
    routeKey: string
  }
  TrucklistResponse: { response: TrucklistResponseType }
  TrucklistResponseError: { error: Error | null }
}

// Load filters from local storage
const locallySavedFilters = loadSavedFilters()

export const GlobalStateStore = createStore<
  GlobalStateType,
  GlobalStateEventTypeMap
>(
  {
    trucklist: {
      isLoading: false,
      initialLoad: false,
      response: undefined,
      error: null,
      filters: {
        ...DefaultFilters,
        ...locallySavedFilters
      },
      lastSearchFilters: {
        ...DefaultFilters,
        ...locallySavedFilters
      }
    }
  },
  {
    TrucklistSearch: (_context) => {
      return {
        trucklist: {
          ..._context.trucklist,
          isLoading: true,
          response: undefined,
          error: null,
          incorrectAccountType: false,
          lastSearchFilters: {
            ..._context.trucklist.filters
          }
        }
      }
    },
    // Manages all the filters on the Trucklist
    TrucklistUpdateFilters: (_context, event) => {
      const { fieldMap } = event
      const updatedFilters = {
        ..._context.trucklist.filters,
        ...fieldMap
      }

      // Save specific filters to local storage
      saveFiltersToLocalStorage(getFiltersToSave(updatedFilters))

      return {
        trucklist: {
          ..._context.trucklist,
          filters: updatedFilters
        }
      }
    },
    TrucklistClearFilters: (_context) => {
      // Reset specific filters in local storage
      saveFiltersToLocalStorage(getFiltersToSave(DefaultFilters))

      return {
        trucklist: {
          ..._context.trucklist,
          isLoading: true,
          filters: DefaultFilters
        }
      }
    },
    TrucklistLoad: (_context, event) => {
      const { routeKey } = event
      return {
        trucklist: {
          ..._context.trucklist,
          routeKey,
          isLoading: true,
          initialLoad: true,
          response: undefined,
          error: null
        }
      }
    },
    TrucklistResponseError: (_context, event) => {
      const { error } = event
      const errorResponse = getErrorResponse(error)
      if (!errorResponse) {
        return _context
      }

      const incorrectAccountType = errorResponse.status === 403
      console.error({ error, errorResponse, incorrectAccountType })
      return {
        trucklist: {
          ..._context.trucklist,
          error,
          incorrectAccountType
        }
      }
    },
    TrucklistResponse: (_context, event) => {
      return {
        trucklist: {
          ..._context.trucklist,
          isLoading: false,
          response: event.response,
          error: null
        }
      }
    }
  }
)

export const useGlobalStateStore = () => useSelector(GlobalStateStore, (state) => state)

export const useTrucklistStateStore = () => useSelector(GlobalStateStore, (state) => state.context.trucklist)

export const useTrucklistFilterStateStore = () =>
  useSelector(GlobalStateStore, (state) => state.context.trucklist.filters)

/**
 * User machine context.
 */
export const UserContext = createActorContext(userMachine, {
  logger: machineLogger
})

/**
 * Hook for accessing the User machine context.
 */
export const useUserContext = () => UserContext.useActorRef()

export const useAuthState = (): AuthStateViewType =>
  UserContext.useSelector((state) => ({
    authIsLoading: state.context.authIsLoading,
    authError: state.context.authError,
    authIsAuthenticated: state.context.authIsAuthenticated,
    authUserClaims: state.context.authUserClaims,
    authPermissions: state.context.authPermissions,
    authContext: state.context.authContext,
    courierToken: state.context.courierToken,
    onboardingStepNeeded: state.context.onboardingStepNeeded,
    userProfile: state.context.userProfile,
    isBroker: state.context.isBroker,
    isCarrier: state.context.isCarrier,
    isStaff: state.context.isStaff,
    isAppAccessAllowed: state.context.isAppAccessAllowed,
    recsEnabled: state.context.recsEnabled
  }))
